import { Box, Typography, useTheme, alpha } from "@mui/material";
import { Savings, ShowChart, TrendingUp, Sell, Paid } from "@mui/icons-material";
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Tooltip,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
} from "recharts";
import { DashboardMetricCard } from "../../Dashboard/components/DashboardMetricCard";
import { useResponsive } from "../../../core/hooks/useResponsive";

function formatInr(n, compact = false) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: compact ? 0 : 2 }).format(Number(n));
}

const compactAxis = (v) => {
  const n = Number(v);
  return Math.abs(n) >= 1000 ? `${(n / 1000).toFixed(1)}k` : n.toFixed(0);
};

const accentFor = (v) => (v > 0 ? "success" : v < 0 ? "error" : "primary");
const toneFor = (v) => (v > 0 ? "success.main" : v < 0 ? "error.main" : "text.primary");

function ChartCard({ title, children }) {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  return (
    <Box sx={{ borderRadius: 2, p: 1.5, minWidth: 0, border: `1px solid ${alpha(primary, 0.12)}`, background: alpha(primary, 0.03) }}>
      <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ letterSpacing: "0.04em", textTransform: "uppercase" }}>
        {title}
      </Typography>
      <Box sx={{ width: "100%", height: 170, mt: 0.5 }}>{children}</Box>
    </Box>
  );
}

/**
 * Sale summary: the run's money figures as tiles (invested / hold value / est. P/L / sold / actual
 * P/L), then value split, P/L snapshot and top holdings by P/L - side by side on desktop, stacked on
 * phones.
 */
export const SaleSummary = ({ data, holdings = [] }) => {
  const theme = useTheme();
  const { isCompact } = useResponsive();
  const primary = theme.palette.primary.main;
  const success = theme.palette.success.main;
  const error = theme.palette.error.main;

  if (!data) return null;

  const soldValue = Number(data.sold) || 0;
  const remainingValue = Number(data.holdValue) || 0;
  const totalCurPL = Number(data.totalCurPL) || 0;
  const actualPL = Number(data.actualPL) || 0;

  const pieData = [
    { name: "Sold", value: soldValue, color: success },
    { name: "Remaining", value: remainingValue, color: alpha(primary, 0.65) },
  ].filter((d) => d.value > 0);

  const snapshot = [
    { k: "Actual", v: actualPL },
    { k: "Cur.", v: totalCurPL },
    { k: "Total", v: actualPL + totalCurPL },
  ];

  const topHoldings = (holdings || [])
    .map((h) => {
      const pl = h.actualPL != null ? Number(h.actualPL) : Number(h.curPL);
      return { symbol: h.symbol, pl: Number.isNaN(pl) ? 0 : pl };
    })
    .sort((a, b) => Math.abs(b.pl) - Math.abs(a.pl))
    .slice(0, 7);

  const grid = <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.9)} vertical={false} />;
  const yAxis = <YAxis tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} width={48} tickFormatter={compactAxis} />;
  const zero = <ReferenceLine y={0} stroke={alpha(theme.palette.text.primary, 0.35)} strokeWidth={1} />;

  return (
    <Box>
      <Box sx={{ display: "grid", gap: 1.25, mb: 1.5, gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(3, minmax(0, 1fr))", lg: "repeat(5, minmax(0, 1fr))" } }}>
        <DashboardMetricCard dense={isCompact} label="Invested" value={formatInr(data.invested, isCompact)} icon={Savings} accent="primary" />
        <DashboardMetricCard dense={isCompact} label="Hold value" value={formatInr(data.holdValue, isCompact)} icon={ShowChart} accent="primary" />
        <DashboardMetricCard dense={isCompact} label="Cur. P/L" value={formatInr(totalCurPL, isCompact)} icon={TrendingUp} accent={accentFor(totalCurPL)} valueColor={toneFor(totalCurPL)} highlight />
        <DashboardMetricCard dense={isCompact} label="Sold" value={formatInr(data.sold, isCompact)} icon={Sell} accent="secondary" />
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
          <DashboardMetricCard dense={isCompact} label="Actual P/L" value={formatInr(actualPL, isCompact)} icon={Paid} accent={accentFor(actualPL)} valueColor={toneFor(actualPL)} highlight />
        </Box>
      </Box>

      <Box sx={{ display: "grid", gap: 1.25, gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" } }}>
        <ChartCard title="Value split">
          {pieData.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Tooltip formatter={(v) => formatInr(v)} contentStyle={{ borderRadius: 12, border: `1px solid ${alpha(primary, 0.18)}` }} />
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={45} outerRadius={70} paddingAngle={3}>
                  {pieData.map((entry) => (
                    <Cell key={entry.name} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="caption" color="text.secondary">No value yet</Typography>
          )}
        </ChartCard>

        <ChartCard title="P/L snapshot">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={snapshot} margin={{ left: 6, right: 6, top: 10, bottom: 6 }}>
              {grid}
              <XAxis dataKey="k" tick={{ fontSize: 11, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} />
              {yAxis}
              {zero}
              <Tooltip formatter={(v) => formatInr(v)} contentStyle={{ borderRadius: 12 }} />
              <Bar dataKey="v" radius={[8, 8, 0, 0]}>
                {snapshot.map((d) => (
                  <Cell key={d.k} fill={d.v >= 0 ? success : error} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Top holdings by P/L">
          {topHoldings.length ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={topHoldings} margin={{ left: 6, right: 6, top: 10, bottom: 6 }}>
                {grid}
                <XAxis dataKey="symbol" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} axisLine={false} tickLine={false} interval={0} />
                {yAxis}
                {zero}
                <Tooltip formatter={(v) => formatInr(v)} contentStyle={{ borderRadius: 12 }} />
                <Bar dataKey="pl">
                  {topHoldings.map((d, i) => (
                    <Cell key={`${d.symbol}-${i}`} fill={d.pl >= 0 ? success : error} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <Typography variant="caption" color="text.secondary">No holdings yet</Typography>
          )}
        </ChartCard>
      </Box>
    </Box>
  );
};
